/**
 * Common formatting utilities for the Smart Insight application
 */

// Format number with Persian locale
export const formatNumber = (num: any): string => {
  if (num === undefined || num === null || isNaN(num)) return '0';
  return new Intl.NumberFormat('fa-IR').format(num);
};

// Convert Latin digits in a value to Persian digits (without grouping separators)
export const toPersianDigits = (value: number | string): string =>
  String(value ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

// Format percentage
export const formatPercentage = (num: any): string => {
  if (num === undefined || num === null || isNaN(num)) return '0%';
  return `${Number(num).toFixed(2)}%`;
};

// Format currency with Persian locale
export const formatCurrency = (num: any, currency: string = 'تومان'): string => {
  if (num === undefined || num === null || isNaN(num)) return `0 ${currency}`;
  if(currency === "ریال")
    num *= 10;
  return `${formatNumber(num)} ${currency}`;
};

// Format currency with separated number and currency for enhanced display
export const formatCurrencyParts = (num: any, currency: string = 'تومان'): { number: string; currency: string } => {
  if (num === undefined || num === null || isNaN(num)) return { number: '0', currency };
  return { number: formatNumber(num), currency };
};

// Format month names for better display
export const formatMonthName = (monthStr: string): string => {
  if (!monthStr) return '';
  // Remove any year prefix (e.g., "03-", "04-", etc.)
  return monthStr.replace(/^\d{2}-/, '');
};

// Get year from month string
export const getYearFromMonth = (monthStr: string): string => {
  if (!monthStr || !monthStr.includes('-')) return '';
  const yearPrefix = monthStr.split('-')[0];
  // Convert 03 to 1403, 04 to 1404, etc.
  return yearPrefix ? `14${yearPrefix}` : '';
};

// Format month name with year for display
export const formatMonthWithYear = (monthStr: string): string => {
  if (!monthStr) return '';
  const monthName = formatMonthName(monthStr);
  const year = getYearFromMonth(monthStr);
  return year ? `${monthName} ${year}` : monthName;
};

// Calculate average from array of numbers
export const calculateAverage = (values: number[]): number => {
  if (!values.length) return 0;
  const sum = values.reduce((acc, val) => acc + (val || 0), 0);
  return sum / values.length;
};

// Round number to specified decimal places
export const roundNumber = (num: number, decimals: number = 1): number => {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export const SUBSCRIPTION_PERCENTAGE_DECIMALS = 1;

export const roundSubscriptionPercentage = (num: number | null | undefined): number | null => {
  if (num === null || num === undefined || Number.isNaN(num)) return null;
  return roundNumber(num, SUBSCRIPTION_PERCENTAGE_DECIMALS);
};

export const formatSubscriptionPercentage = (num: number | null | undefined): string => {
  if (num === null || num === undefined || Number.isNaN(num)) return '-';
  return `${Number(num).toFixed(SUBSCRIPTION_PERCENTAGE_DECIMALS)}%`;
};

export const toSubscriptionPercentage = (value?: number | null): number | null => {
  if (value === null || value === undefined) return null;
  return roundSubscriptionPercentage(value);
};

export const computeWeightedSubscriptionPercentage = (
  items: Array<{ percentage: number | null; factureCount: number | null }>
): number | null => {
  let weightedSum = 0;
  let weightTotal = 0;

  items.forEach(({ percentage, factureCount }) => {
    if (percentage != null && factureCount != null) {
      weightedSum += percentage * factureCount;
      weightTotal += factureCount;
    }
  });

  return weightTotal > 0 ? roundSubscriptionPercentage(weightedSum / weightTotal) : null;
};